import React, { useEffect, useMemo, useState } from 'react'
import { CalendarClock, CheckCircle2, ShieldCheck } from 'lucide-react'
import { BookDemoScheduler } from '@/components/BookDemoScheduler'
import SetupModeShell from './SetupModeShell'
import useSetupMode from '../../hooks/useSetupMode'
import { readDraft, writeDraft } from '../signup/signupState'
import supabase from '../../supabaseClient'
import {
  formatSlotLabel,
  getUserTimezone,
} from '@/lib/calendlyBooking'
import { persistSignupUserOnActivationCallBooked } from '../../lib/signupUserPersist'

const PERSONA_AGENDA = {
  community: [
    'Confirm camera + venue setup',
    'Pricing tailored to your weekly volume',
    'Schedule your first 2–3 games together',
    'Walk-through of the community portal',
  ],
  academy: [
    'Confirm session schedule + court access',
    'Pricing tailored to team count',
    'Walk-through of the coach + player view',
    'Set up your first squads',
  ],
  venue: [
    'Confirm court count + camera install',
    'Revenue share model + onboarding plan',
    'Walk-through of the venue dashboard',
    'Set go-live date for your first court',
  ],
}

function personaToAudience(persona) {
  if (persona === 'academy' || persona === 'venue' || persona === 'community') {
    return persona
  }
  return 'community'
}

export default function SetupModeBookCall({ persona }) {
  const {
    persona: draftPersona,
    activationCallBooked,
    activationCallBookedAt,
    orgName,
    fullName,
    refresh,
  } = useSetupMode()
  const effectivePersona = persona || draftPersona || 'community'
  const audience = personaToAudience(effectivePersona)
  const agenda = PERSONA_AGENDA[effectivePersona] || PERSONA_AGENDA.community

  const draft = useMemo(readDraft, [])
  const [name, setName] = useState(fullName || draft.fullName || '')
  const [email, setEmail] = useState(draft.email || '')
  const [bookedSlot, setBookedSlot] = useState(
    activationCallBookedAt || '',
  )

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return
      if (session?.user?.email && !email) {
        setEmail(session.user.email)
      }
    })
    return () => {
      cancelled = true
    }
  }, [email])

  const handleBooked = async (result, lead) => {
    const iso = result.start_time || new Date().toISOString()
    writeDraft({
      activationCallBooked: true,
      activationCallBookedAt: iso,
      ...(lead?.phone?.trim() ? { phone: lead.phone.trim() } : {}),
    })
    if (effectivePersona === 'academy' || effectivePersona === 'venue' || effectivePersona === 'community') {
      const latest = readDraft()
      const persist = await persistSignupUserOnActivationCallBooked({
        persona: effectivePersona,
        fullName: lead?.name || name || latest.fullName || '',
        email: lead?.email || email || latest.email || '',
        phone: lead?.phone || '',
        organizationName:
          lead?.organization || orgName || latest.organizationName || '',
        bookedAt: iso,
      })
      if (!persist.ok) {
        console.warn('SetupModeBookCall: Users row not saved', persist.error)
      }
    }
    setBookedSlot(iso)
    refresh()
  }

  const timezone = getUserTimezone()
  const isBooked = !!(bookedSlot || activationCallBooked)

  return (
    <SetupModeShell persona={effectivePersona} active="book-activation-call">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#71cdd9]/10 border border-[#71cdd9]/30 text-[12px] font-medium text-[#0e6e7a] mb-5">
          <CalendarClock size={12} aria-hidden />
          30 minutes · free · just a chat
        </div>
        <h1 className="text-[28px] sm:text-[34px] font-semibold tracking-tight leading-[1.15] text-slate-900 mb-3">
          Let&apos;s find a time to get you going
        </h1>
        <p className="text-[15px] sm:text-[16px] text-slate-600 max-w-[620px] leading-relaxed">
          Pick a time that feels easy — we&apos;ll take it from there. Most people
          book within a day of signing up.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-8 items-start">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden min-h-[420px]">
          {isBooked ? (
            <div className="p-10 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-5">
                <CheckCircle2 size={26} className="text-emerald-600" aria-hidden />
              </div>
              <h2 className="text-[22px] font-semibold tracking-tight text-slate-900 mb-2">
              You&apos;re in — see you soon.
              </h2>
              {bookedSlot && (
                <p className="text-[16px] font-medium text-slate-800 mb-2">
                  {formatSlotLabel(bookedSlot, timezone)}
                </p>
              )}
              <p className="text-[14px] text-slate-600 max-w-[420px] mx-auto leading-relaxed">
                {email
                  ? `Invite sent to ${email}. Looking forward to meeting you.`
                  : "Check your inbox for the calendar invite. Looking forward to meeting you."}
              </p>
            </div>
          ) : (
            <BookDemoScheduler
              audience={audience}
              variant="light"
              layout="full"
              name={name}
              email={email}
              onNameChange={setName}
              onEmailChange={setEmail}
              onBooked={handleBooked}
              defaultOrganization={orgName}
              confirmLabel="Book this time"
              footerNote="No pressure. We'll only move forward if it's a fit."
              organizationPlaceholder={
                effectivePersona === 'academy'
                  ? 'Academy name'
                  : effectivePersona === 'venue'
                    ? 'Venue name'
                    : 'Community name'
              }
            />
          )}
        </div>

        <aside className="lg:sticky lg:top-[110px] space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="text-[12.5px] uppercase tracking-[0.2em] text-[#0e6e7a] mb-3">
              What we'll cover together
            </h3>
            <ul className="space-y-2.5">
              {agenda.map((line, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-[13.5px] text-slate-700 leading-snug">
                  <CheckCircle2
                    size={15}
                    className="text-[#71cdd9] mt-0.5 shrink-0"
                    aria-hidden
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0">
                <ShieldCheck size={16} className="text-emerald-600" aria-hidden />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-slate-900 leading-snug mb-1">
                  No pressure, no commitment.
                </p>
                <p className="text-[12.5px] text-slate-600 leading-relaxed">
                  We'll only move forward together if Statbox is the right fit for your operation.
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </SetupModeShell>
  )
}
